'use client';

import React from 'react';
import Image from 'next/image';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'next-intl';
import { useLegalGuaranteeContent } from '@/ui/providers/legal-guarantee-provider';

interface LegalGuaranteeModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Crystallize CDN url of the notice for the current language, supplied by the badge. */
  noticeUrl: string;
}

/**
 * Overlay showing the official EU legal guarantee notice. The notice is an A4 artwork whose text is
 * fully outlined, so it exposes nothing to assistive technology — `noticeAlt` names the document and
 * the europa.eu link below it is the actual text alternative (it also makes the QR code printed on
 * the notice usable on desktop).
 */
export function LegalGuaranteeModal({
  isOpen,
  onClose,
  noticeUrl,
}: Readonly<LegalGuaranteeModalProps>) {
  const t = useTranslations('LegalGuarantee');
  // Crystallize is the source of record for this copy; the Weblate namespace is the fallback for
  // any field the editor has not authored, and for the whole document while it does not exist.
  const cmsContent = useLegalGuaranteeContent();
  const modalTitle = cmsContent?.modalTitle || t('modalTitle');
  const noticeAlt = cmsContent?.noticeAlt || t('noticeAlt');
  const consumerRightsUrl = cmsContent?.consumerRightsUrl || t('consumerRightsUrl');
  const consumerRightsLabel = cmsContent?.consumerRightsLabel || t('consumerRightsLabel');

  // `open` rather than an early return: Headless UI already renders nothing while closed, so the
  // ~600 kB notice still stays out of the DOM and off the network until the badge is clicked — but
  // unmounting the Dialog ourselves would rob it of the chance to return focus to the badge.
  return (
    <Dialog open={isOpen} className="relative z-50" onClose={onClose}>
      <div className="fixed inset-0 bg-neutral-100/50" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-2 sm:p-4">
        <Dialog.Panel className="relative flex max-h-[95vh] w-full max-w-3xl flex-col rounded-2xl bg-white p-4 shadow-xl sm:p-6">
          <Dialog.Title as="h2" className="pr-10 text-lg font-bold">
            {modalTitle}
          </Dialog.Title>

          <button
            type="button"
            onClick={onClose}
            aria-label={t('close')}
            className="absolute right-3 top-3 rounded-xl p-1 duration-200 ease-in hover:bg-neutral-10"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>

          <div className="mt-4 flex-1 overflow-y-auto overscroll-contain">
            <Image
              src={noticeUrl}
              alt={noticeAlt}
              width={595}
              height={842}
              className="h-auto w-full"
            />
          </div>

          <a
            href={consumerRightsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 shrink-0 text-sm underline"
          >
            {consumerRightsLabel}
          </a>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
